import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import FlatpickrInput from './FlatpickrInput';
import StyledSelect from './StyledSelect';

export default function NewProjectFormScreen({
  isOpen,
  initialName = '',
  onClose,
  onSubmit,
}) {
  const statusOptions = useMemo(() => ['Active', 'On Hold', 'Completed', 'Archived'], []);
  const priorityOptions = useMemo(() => ['Low', 'Medium', 'High', 'Urgent'], []);
  const currencyOptions = useMemo(() => ['PKR', 'USD', 'EUR', 'SAR'], []);
  const statusDropdownOptions = useMemo(
    () => statusOptions.map((item) => ({ value: item, label: item })),
    [statusOptions]
  );
  const priorityDropdownOptions = useMemo(
    () => priorityOptions.map((item) => ({ value: item, label: item })),
    [priorityOptions]
  );
  const currencyDropdownOptions = useMemo(
    () => currencyOptions.map((item) => ({ value: item, label: item })),
    [currencyOptions]
  );

  const [form, setForm] = useState({
    projectName: '',
    clientCompany: '',
    description: '',
    status: 'Active',
    priority: 'Medium',
    budget: '',
    currency: 'PKR',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      projectName: String(initialName || '').trim(),
      clientCompany: '',
      description: '',
      status: 'Active',
      priority: 'Medium',
      budget: '',
      currency: 'PKR',
      startDate: '',
      endDate: '',
    });
    setError('');
    setSaving(false);
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = String(form.projectName || '').trim();
    if (!name) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSubmit?.({
        name,
        client_company: String(form.clientCompany || '').trim(),
        description: String(form.description || '').trim(),
        status: form.status,
        priority: form.priority,
        budget: form.budget === '' ? null : Number(form.budget),
        currency: form.currency,
        start_date: form.startDate || null,
        end_date: form.endDate || null,
        color: '#c44569',
      });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create project');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200 bg-slate-50">
          <h1 className="text-2xl font-bold text-slate-900">New Project</h1>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition disabled:opacity-60"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-6 sm:py-8 space-y-8 overflow-y-auto max-h-[calc(92vh-84px)]">
          {error ? (
            <div className="rounded-xl px-4 py-3 text-sm border-l-4" style={{ background: '#fce0e8', color: '#c44569', borderLeftColor: '#c44569' }}>
              {error}
            </div>
          ) : null}

          <section>
            <h2 className="text-sm font-extrabold tracking-wider text-slate-400 uppercase mb-4">
              Basic Info
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-slate-700 font-medium mb-2">
                  Project Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="projectName"
                  value={form.projectName}
                  onChange={handleChange}
                  placeholder="e.g. K2 Pickleball"
                  disabled={saving}
                  className="w-full h-12 rounded-xl border border-slate-300 px-4 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-medium mb-2">Client / Company</label>
                <input
                  type="text"
                  name="clientCompany"
                  value={form.clientCompany}
                  onChange={handleChange}
                  placeholder="Client name (optional)"
                  disabled={saving}
                  className="w-full h-12 rounded-xl border border-slate-300 px-4 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-medium mb-2">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Brief description of the project..."
                  disabled={saving}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition resize-none disabled:bg-slate-50"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-extrabold tracking-wider text-slate-400 uppercase mb-4">
              Status & Priority
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-700 font-medium mb-2">Status</label>
                <StyledSelect
                  value={form.status}
                  onChange={(next) => setForm((prev) => ({ ...prev, status: next }))}
                  options={statusDropdownOptions}
                  placeholder="Select status"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block text-slate-700 font-medium mb-2">Priority</label>
                <StyledSelect
                  value={form.priority}
                  onChange={(next) => setForm((prev) => ({ ...prev, priority: next }))}
                  options={priorityDropdownOptions}
                  placeholder="Select priority"
                  disabled={saving}
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-extrabold tracking-wider text-slate-400 uppercase mb-4">
              Financial
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-700 font-medium mb-2">Budget</label>
                <input
                  type="number"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  placeholder="0"
                  disabled={saving}
                  className="w-full h-12 rounded-xl border border-slate-300 px-4 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-medium mb-2">Currency</label>
                <StyledSelect
                  value={form.currency}
                  onChange={(next) => setForm((prev) => ({ ...prev, currency: next }))}
                  options={currencyDropdownOptions}
                  placeholder="Select currency"
                  disabled={saving}
                  searchable
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-extrabold tracking-wider text-slate-400 uppercase mb-4">
              Timeline
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-slate-700 font-medium mb-2">Start Date</label>
                <FlatpickrInput
                  value={form.startDate}
                  onChange={(date) => setForm((prev) => ({ ...prev, startDate: date }))}
                  disabled={saving}
                  className="!h-12 !rounded-xl !border-slate-300 !bg-white !px-4 !text-slate-900 disabled:!bg-slate-50"
                  options={{ dateFormat: 'Y-m-d', animate: true, disableMobile: false }}
                />
              </div>

              <div>
                <label className="block text-slate-700 font-medium mb-2">End Date</label>
                <FlatpickrInput
                  value={form.endDate}
                  onChange={(date) => setForm((prev) => ({ ...prev, endDate: date }))}
                  disabled={saving}
                  className="!h-12 !rounded-xl !border-slate-300 !bg-white !px-4 !text-slate-900 disabled:!bg-slate-50"
                  options={{ dateFormat: 'Y-m-d', animate: true, disableMobile: false }}
                />
              </div>
            </div>
          </section>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow-sm disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Creating...
                </>
              ) : (
                'Create Project'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
